// Essentials
import ReactDOMServer from 'react-dom/server';
import React, {
    useEffect,
    useContext,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import { MapContainer, Marker, TileLayer, ZoomControl } from 'react-leaflet';
import i18n from '../../i18n';

// Components
import { LocationContext } from '../LocationContext/LocationContext';
import LocationMapPopup from './LocationMapPopup';
import ClusteredMarkers from './ClusteredMarkers';
import CustomMarker from '../CustomMarker/CustomMarker';

// Handler
import MapPositionHandler from './MapPositionHandler';

// Styles
import styles from './LocationMap.module.scss';

// Map Basics
import {
    MAX_ZOOM,
    MIN_ZOOM,
    MAP_DEFAULTS,
} from './BasicMapProps';

// Icon
import DefaultIcon from '../../assets/images/icons/DefaultIcon';

L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);


// eslint-disable-next-line react/prop-types
const LocationMap = ({ initialZoomLevel, map }) => {

    const {
        setOpenedLocationId,
        uniquePlace,
        setUniquePlace,
        locationObjects,
        openedLocationId,
    } = useContext(LocationContext);

    const isMountedRef = useRef(true);
    const isMounted = useCallback(() => isMountedRef.current, []);
    const markerRef = useRef();
    const markerClick = useCallback(
        ({ locationLat, locationLon, locationId }) => {
            if (isMounted()) {
                setUniquePlace({
                    locationLat: locationLat,
                    locationLon: locationLon,
                    place_id: locationId,
                });

                setOpenedLocationId(locationId);
            }
        },
        [isMounted, setOpenedLocationId, setUniquePlace],
    );

    useEffect(() => {
        isMountedRef.current = true;

        return () => (isMountedRef.current = false);
    }, []);

    const markerDom = useMemo(() => {

        return locationObjects?.map((marker) => {
            const {
                id,
                lat,
                lon,
                name,
            } = marker;

            const markerAction = () => markerClick({
                // name,
                locationLat: lat,
                locationLon: lon,
                locationId: id,
            });

            return (
                <Marker
                    key={id}
                    ref={markerRef}
                    eventHandlers={{
                        click: markerAction,
                        keydown: (event) => event.originalEvent.key === 'Enter' && markerAction(),
                        mouseover: (event) => {
                            event.target.openPopup();
                        },
                        mouseout: (event) => event.target.closePopup(),
                    }}
                    position={[lat, lon]}
                    icon={L.divIcon({
                        className: styles.customMarker,
                        html: ReactDOMServer.renderToString(
                            <CustomMarker
                                title={`${name}`}
                                description={i18n.t('locationMap.location')}
                            >
                                <DefaultIcon />
                            </CustomMarker>,
                        ),
                    })}
                    marker={marker}
                />
            );
        });
    }, [locationObjects, markerClick]);

    return (
        <>
            <section className={styles.mapContainer}>
                <LocationMapPopup />
                <MapContainer
                    center={[MAP_DEFAULTS.lat, MAP_DEFAULTS.lng]}
                    zoom={initialZoomLevel}
                    scrollWheelZoom={false}
                    className={styles.map}
                    updateWhenZooming={true}
                    gestureHandling={true}
                    gestureHandlingOptions={{
                        duration: 1500,
                        text: {
                            touch: 'Benutzen Sie zwei Finger um die Karte zu bewegen.',
                            scroll: 'Benutzen Sie \'STRG\' + \'Scroll\' um in der Karte zu zoomen.',
                            scrollMac: 'Benutzen Sie \'\u2318\' + \'Scroll\' um in der Karte zu zoomen.',
                        },
                    }}
                    minZoom={MIN_ZOOM}
                    maxZoom={MAX_ZOOM}
                    zoomSnap={0.5}
                    zoomDelta={0.5}
                    zoomControl={false}
                >
                    <TileLayer
                        url="https://{s}.piano.tiles.quaidorsay.fr/en/{z}/{x}/{y}.png"
                    />
                    <ZoomControl position="bottomright" />
                    <MapPositionHandler
                        panPosition={uniquePlace}
                        markers={locationObjects}
                        openedLocationId={openedLocationId}
                    />
                    {markerDom?.length > 0 && (
                        <ClusteredMarkers
                            map={map}
                            locationObjects={locationObjects}
                            markerClick={markerClick}
                        />
                    )}
                </MapContainer>
            </section>
        </>
    );
};

export default LocationMap;
